import { useEffect, useMemo, useRef, useState } from 'react';
import { Pencil, Users } from 'lucide-react';
import { Card } from '@components/base/layout';
import { Text, Badge, Icon, Tooltip } from '@components/base/display';
import type { BadgeSize, BadgeVariant } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Alert, Spinner } from '@components/base/feedback';
import {
  DataList,
  useDataList,
  dataListWorkflowRegistry,
} from '@components/integrated/data-list';
import type { DataListConfig } from '@components/integrated/data-list';
import type { ActionsConfig, CustomActionConfig } from '@components/integrated/entity-form';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromDataList,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { intervenantService, type ClaimIntervenantRow } from '../api/intervenantService';
import { useClaimDataStore } from './claimDataStore';
import { focusCardAnchor } from './useActionInjection';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface IntervenantsCardProps {
  /** Internal claim id — the card (re)fetches the claim's intervenants from it. */
  idSin: string;
  /** Veos session id, sent as `SES` with the `WS2_GS_TAB_INTERVENANTS` call. */
  ses: string;
  /**
   * Per-tenant layout overrides injected by the `withComponentRules` HOC.
   * Underscored to match the injected meta-prop convention; `undefined` at
   * baseline ⇒ the config is returned unchanged (byte-identical render).
   * Rule authors target stable keys only:
   *
   * - `columns` — visible `categ` / `num` / `nom` / `prenom` / `email` /
   *   `tel` / `action`, hidden by default `idPer` / `idSinp` (revealed with
   *   `showColumn`): rename / hide / show / reorder / tooltip;
   * - `actions` — the per-row `edit` pencil ("Modifier l'intervenant", its
   *   accessible name + tooltip): relabel / hide;
   * - `title` — the card title.
   *
   * The card keeps owning the fetch and the pencil's edit request, so an
   * override can never affect them.
   */
  _layoutOverrides?: LayoutOverrides;
}

/** Stable registry / layout-config id of this card. */
const COMPONENT_ID = 'claimflow.intervenantsCard';

/**
 * Registry id (and self-anchor) of the « Modifier un intervenant » card
 * (`EditIntervenantCard`, mahhp 22919) the pencil brings into view.
 */
const EDIT_INTERVENANT_CARD_ID = 'claimflow.editIntervenantCard';

/** Card-level texts (title / states), kept out of the JSX. */
const INTERVENANTS_CARD_TEXT = {
  title: 'Intervenants',
  countTooltip: "Nombre d'intervenants",
  loadError: 'Erreur lors du chargement des intervenants.',
  empty: 'Aucun intervenant',
} as const;

/** Design-system pill of the "Catég." column (mockup: navy-tinted tag). */
const CATEG_BADGE: { variant: BadgeVariant; size: BadgeSize } = {
  variant: 'primary',
  size: 'sm',
};

// ---------------------------------------------------------------------------
// Row action config
//
// The per-row pencil is a custom action keyed `edit`, published under
// `actions` and merged with `_layoutOverrides.actions` (`setActionLabel` /
// `hideAction`) — same convention as the « Tâches » card's "Clôturer" button.
// Its `label` is the icon-only button's accessible name AND tooltip. The card
// wires the click itself; `handler` only names it. It is rendered by a custom
// cell renderer (DataList `rowActions` render label-only buttons).
// ---------------------------------------------------------------------------

const EDIT_ACTION_KEY = 'edit';

const EDIT_ACTION = {
  id: EDIT_ACTION_KEY,
  handler: 'claimflow-intervenantEdit',
  label: "Modifier l'intervenant",
  variant: 'ghost',
  size: 'sm',
} satisfies CustomActionConfig;

const INTERVENANTS_ACTIONS: ActionsConfig = { custom: [EDIT_ACTION] };

/** The pencil as resolved for this render (overrides applied). */
interface ResolvedEditAction {
  label: string;
  visible: boolean;
}

/**
 * Pencil click: ask the « Modifier un intervenant » card to edit this
 * intervenant (it selects `ID_SINP` in its « Intervenant » select and loads
 * it), then bring that card into view (scroll + expand + highlight). When the
 * edit card is not mounted (e.g. hidden by a tenant rule) the focus is a
 * no-op; the request stays pending until an edit card mounts and takes it.
 */
function editIntervenant(idSinp: number): void {
  useClaimDataStore.getState().requestEditIntervenant(idSinp);
  focusCardAnchor(EDIT_INTERVENANT_CARD_ID);
}

// ---------------------------------------------------------------------------
// Per-row render context
//
// The custom cell renderers are module singletons (registered once below),
// but the resolved pencil (label / visibility overrides) belongs to the card
// instance. Each row carries it under `__ctx`, read from `row` at render time.
// Rows are rebuilt whenever it changes (see `rows` useMemo) — no module-global
// mutable state, multi-instance safe (mirrors `ClaimTasksCard`).
// ---------------------------------------------------------------------------

interface IntervenantRowContext {
  editAction: ResolvedEditAction;
}

interface IntervenantListRow extends ClaimIntervenantRow {
  /** Stable React key for the DataList table (it keys rows by `row.id`). */
  id: number;
  __ctx: IntervenantRowContext;
}

function getRowContext(row: Record<string, unknown>): IntervenantRowContext | undefined {
  return (row as Partial<IntervenantListRow>).__ctx ?? undefined;
}

// ---------------------------------------------------------------------------
// Custom cell renderers (registered once at module load)
// ---------------------------------------------------------------------------

const CATEG_CELL_RENDERER = 'claimflow-intervenantCateg';
const ACTION_CELL_RENDERER = 'claimflow-intervenantEdit';

// "Catég." column: the category code as a design-system Badge (uppercase tag,
// as on the mockup). A row without a category renders nothing.
dataListWorkflowRegistry.registerCellRenderer(CATEG_CELL_RENDERER, (_value, row) => {
  const categ = (row as unknown as ClaimIntervenantRow).CD_CATEG;
  if (categ == null || categ === '') return null;
  return (
    <Badge variant={CATEG_BADGE.variant} size={CATEG_BADGE.size}>
      {categ}
    </Badge>
  );
});

// Pencil column: an icon-only ghost button named + tooltipped from the `edit`
// action. The click never reaches the row (`stopPropagation`).
dataListWorkflowRegistry.registerCellRenderer(ACTION_CELL_RENDERER, (_value, row) => {
  const intervenant = row as unknown as ClaimIntervenantRow;
  const ctx = getRowContext(row);
  if (!ctx?.editAction.visible) return null;
  return (
    <Tooltip content={ctx.editAction.label}>
      <Button
        variant={EDIT_ACTION.variant}
        size={EDIT_ACTION.size}
        leftIcon={<Icon icon={Pencil} size="sm" />}
        aria-label={ctx.editAction.label}
        onClick={(event) => {
          event.stopPropagation();
          editIntervenant(intervenant.ID_SINP);
        }}
      />
    </Tooltip>
  );
});

// ---------------------------------------------------------------------------
// Config
//
// Declarative DataListConfig with stable column keys; French labels live only
// in `column.label` (display-only / overridable). Each key reads its raw
// `WS2_GS_TAB_INTERVENANTS` field through `dataSource.fieldMapping` — what the
// cell displays AND what the client-side sort compares (a `null` sorts last).
// The list keeps the API order until the user clicks a header (none → asc →
// desc). A `null` text cell renders empty, as in the sibling cards.
// ---------------------------------------------------------------------------

/**
 * Header style of the mockup (normal case, secondary tone) — same override as
 * the « Comptabilité » card's columns.
 */
const HEADER_CLASSNAME =
  '!normal-case !text-xs !font-medium !tracking-normal text-secondary-500';

function createIntervenantsListConfig(rows: IntervenantListRow[]): DataListConfig {
  return {
    id: COMPONENT_ID,

    dataSource: {
      data: rows as unknown as Record<string, unknown>[],
      // Column key → raw VEOS field (what the cell displays AND sorts on).
      fieldMapping: {
        categ: 'CD_CATEG',
        num: 'NUM_PER',
        nom: 'NOM_PER',
        prenom: 'PRENOM_PER',
        email: 'EMAIL_PER',
        tel: 'TELPRINC_PER',
        idPer: 'ID_PER',
        idSinp: 'ID_SINP',
      },
    },

    columns: {
      // --- Visible by default (mockup order) -------------------------------
      categ: {
        label: 'Catég.',
        headerTooltip: "Catégorie de l'intervenant",
        order: 1,
        align: 'left',
        type: 'custom',
        typeOptions: { renderer: CATEG_CELL_RENDERER },
        headerClassName: HEADER_CLASSNAME,
        sortable: true,
      },
      num: {
        label: 'No',
        headerTooltip: "Numéro de l'intervenant",
        order: 2,
        align: 'left',
        type: 'text',
        headerClassName: HEADER_CLASSNAME,
        className: 'text-secondary-500',
        sortable: true,
      },
      nom: {
        label: 'Nom',
        order: 3,
        align: 'left',
        type: 'text',
        headerClassName: HEADER_CLASSNAME,
        className: 'font-semibold',
        sortable: true,
      },
      prenom: {
        label: 'Prénom',
        order: 4,
        align: 'left',
        type: 'text',
        headerClassName: HEADER_CLASSNAME,
        sortable: true,
      },
      email: {
        label: 'Email',
        order: 5,
        align: 'left',
        type: 'text',
        headerClassName: HEADER_CLASSNAME,
        sortable: true,
      },
      tel: {
        label: 'Tél.',
        headerTooltip: 'Téléphone principal',
        order: 6,
        align: 'left',
        type: 'text',
        headerClassName: HEADER_CLASSNAME,
        sortable: true,
      },
      action: {
        label: '',
        order: 7,
        align: 'right',
        type: 'custom',
        typeOptions: { renderer: ACTION_CELL_RENDERER },
        headerClassName: HEADER_CLASSNAME,
        sortable: false,
      },

      // --- Hidden by default (an admin can reveal them with showColumn) ----
      idPer: {
        label: 'N° personne',
        order: 8,
        hidden: true,
        align: 'left',
        type: 'text',
        headerClassName: HEADER_CLASSNAME,
        sortable: true,
      },
      idSinp: {
        label: 'N° intervenant',
        headerTooltip: "Identifiant de l'intervenant sur le sinistre (ID_SINP)",
        order: 9,
        hidden: true,
        align: 'left',
        type: 'text',
        headerClassName: HEADER_CLASSNAME,
        sortable: true,
      },
    },

    display: {
      size: 'compact',
      hoverable: false,
    },

    // Front-only sorting on the loaded rows (no API call); API order until the
    // user clicks a header.
    sorting: {
      enabled: true,
    },

    toolbar: {
      visible: false,
    },

    // Every intervenant on one page — no pager.
    pagination: {
      mode: 'scroll',
      pageSize: 999_999,
    },
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Identity of one fetch: the claim, the session and the intervenants version. */
function buildRequestKey(idSin: string, ses: string, version: number): string {
  return JSON.stringify([idSin, ses, version]);
}

/** The intervenants on screen, tagged with the request they answer. */
interface IntervenantsLoadState {
  /** {@link buildRequestKey} of the request. */
  key: string;
  idSin: string;
  rows: ClaimIntervenantRow[];
  error: string | null;
}

export function IntervenantsCard({
  idSin,
  ses,
  _layoutOverrides,
}: Readonly<IntervenantsCardProps>) {
  // Bumped whenever an intervenant of the claim is added (`IntervenantForm`)
  // or modified (`EditIntervenantCard`) — refetch on it.
  const intervenantsVersion = useClaimDataStore((s) => s.intervenantsVersion);

  const [loaded, setLoaded] = useState<IntervenantsLoadState | null>(null);

  // Stale-response guard: every request takes a new sequence number and only
  // the latest one issued may write state, so a slow response (a claim the
  // user already left, a refresh overtaken by a newer one) never overwrites a
  // newer result.
  const requestRef = useRef(0);

  useEffect(() => {
    // Bumped even without a claim id, so an in-flight response is dropped.
    const requestId = ++requestRef.current;
    if (idSin === '') return;
    const key = buildRequestKey(idSin, ses, intervenantsVersion);
    intervenantService.fetchClaimIntervenants(ses, idSin).then(
      (rows) => {
        if (requestId !== requestRef.current) return;
        setLoaded({ key, idSin, rows, error: null });
      },
      () => {
        if (requestId !== requestRef.current) return;
        setLoaded({ key, idSin, rows: [], error: INTERVENANTS_CARD_TEXT.loadError });
      },
    );
  }, [idSin, ses, intervenantsVersion]);

  // Only the CURRENT claim's intervenants are ever shown: full spinner until
  // its first response settles (first load and after a claim switch). A
  // refetch of the same claim (an intervenant was added / modified) keeps the
  // rows on screen with a discreet header spinner and `aria-busy`. Without a
  // claim id nothing is fetched and the card reads as empty.
  const hasClaim = idSin !== '';
  const current = hasClaim && loaded?.idSin === idSin ? loaded : null;
  const isInitialLoading = hasClaim && current === null;
  const isRefetching =
    current !== null && current.key !== buildRequestKey(idSin, ses, intervenantsVersion);
  const intervenants = useMemo(() => current?.rows ?? [], [current]);
  const loadError = current?.error ?? null;

  // The pencil resolved against the tenant overrides (identity at baseline).
  const editOverride = _layoutOverrides?.actions?.[EDIT_ACTION_KEY];
  const editLabel = editOverride?.label ?? EDIT_ACTION.label;
  const editVisible = editOverride?.visible ?? true;
  const editAction = useMemo<ResolvedEditAction>(
    () => ({ label: editLabel, visible: editVisible }),
    [editLabel, editVisible],
  );

  // Per-render rows: the data + the live per-row context, rebuilt whenever
  // either changes so the renderers read fresh values.
  const rows = useMemo<IntervenantListRow[]>(
    () =>
      intervenants.map((intervenant) => ({
        ...intervenant,
        id: intervenant.ID_SINP,
        __ctx: { editAction },
      })),
    [intervenants, editAction],
  );

  const baseConfig = useMemo(() => createIntervenantsListConfig(rows), [rows]);

  // Publish the columns AND the row action so the layout editor lists every
  // rule target of this card.
  usePublishLayoutConfig(COMPONENT_ID, {
    ...buildPublishedConfigFromDataList(baseConfig, INTERVENANTS_CARD_TEXT.title),
    actions: INTERVENANTS_ACTIONS,
  });

  const config = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );

  const list = useDataList(config);

  return (
    <Card padding="none">
      <Card.Header>
        <div className="flex items-center gap-2">
          <Icon icon={Users} size="md" color="primary" />
          <Text as="span" weight="semibold" size="base">
            {resolveCardTitle(INTERVENANTS_CARD_TEXT.title, _layoutOverrides)}
          </Text>
          {!isInitialLoading && !loadError && (
            <Badge variant="default" size="sm" pill title={INTERVENANTS_CARD_TEXT.countTooltip}>
              {intervenants.length}
            </Badge>
          )}
          {/* A refetch keeps the rows on screen, with this discreet hint. */}
          {isRefetching && <Spinner size="xs" />}
        </div>
      </Card.Header>
      <Card.Content>
        {isInitialLoading && (
          <div className="flex items-center justify-center min-h-24 p-5">
            <Spinner size="md" />
          </div>
        )}

        {!isInitialLoading && loadError && (
          <div className="p-5">
            <Alert variant="error">{loadError}</Alert>
          </div>
        )}

        {!isInitialLoading && !loadError && intervenants.length === 0 && (
          <div className="p-5">
            <Text size="sm" color="muted">
              {INTERVENANTS_CARD_TEXT.empty}
            </Text>
          </div>
        )}

        {!isInitialLoading && !loadError && intervenants.length > 0 && (
          <div data-testid="intervenants-list" aria-busy={isRefetching}>
            <DataList config={config} list={list}>
              <DataList.Table />
            </DataList>
          </div>
        )}
      </Card.Content>
    </Card>
  );
}
